import { useTranslation } from '@mukil/i18n';
import { useAuth } from '../../lib/auth/auth_context';

/**
 * The app's ONE sign-out action: its label and its handler. Every sign-out
 * control renders from this — `SignOutMenuItem` (both account menus) and the
 * Profile screen's Sign out row — rather than each wiring its own
 * `useAuth().signOut` call, so there is exactly one place the label and the
 * handler can be got wrong.
 *
 * That matters more than the line count saved: the bug this guards against
 * was a control with the right label and NO handler at all, which is valid
 * TSX, valid a11y, and invisible to every check in CI. A second hand-wired
 * copy is the same bug waiting to happen again (`docs/design-system/CLAUDE.md`
 * § "One canonical component per status/badge concept" — the same reasoning
 * applied to an action).
 */
export function useSignOutAction(): { label: string; sign_out: () => void } {
  const { t } = useTranslation('wp');
  const { signOut } = useAuth();
  return {
    label: t('account.sign_out'),
    sign_out: () => {
      // Fire-and-forget: `signOut()` never throws (`oauth_pkce.ts` bounds and
      // swallows the best-effort revoke call) and click/select handlers are
      // sync, so there is nothing for a caller to await or recover from. It
      // flips `AuthStatus` to `'signed_out'`, which is what swaps the whole
      // app for `SignedOutScreen` (see `lib/auth/auth_context.tsx`).
      void signOut();
    },
  };
}
